// Copied into your app by RapidRailsUI (bin/rails rui:update).
// Don't edit this copy: the next update overwrites it. Fix it in the rapid_rails_ui gem.
//
// Dropdown menu placement as pure arithmetic over measured boxes (viewport px).
// The menu is position: fixed, so a scrolling or overflow-hidden ancestor (a
// table wrapper) no longer clips it. The controller measures; this decides.
//
// bottom/top: left edges aligned with the trigger, right edges when that would
// cross the viewport; flips to the other side only when that side has room.
// right/left: beside the trigger, top edges aligned, flipped the same way.
// Always clamped inside the viewport margin when the menu fits.

export function placeMenu(trigger, { width, height }, { viewportWidth, viewportHeight }, placement = "bottom", { gap = 8, margin = 8 } = {}) {
  const clamp = (value, size, limit) => Math.min(Math.max(value, margin), Math.max(limit - size - margin, margin))
  const below = trigger.bottom + gap
  const above = trigger.top - gap - height
  const after = trigger.right + gap
  const before = trigger.left - gap - width
  const fitsBelow = below + height <= viewportHeight - margin
  const fitsAbove = above >= margin
  const fitsAfter = after + width <= viewportWidth - margin
  const fitsBefore = before >= margin

  if (placement === "right" || placement === "left") {
    let side = placement
    if (side === "right" && !fitsAfter && fitsBefore) side = "left"
    else if (side === "left" && !fitsBefore && fitsAfter) side = "right"
    return { left: side === "right" ? after : before, top: clamp(trigger.top, height, viewportHeight), side }
  }

  let side = placement === "top" ? "top" : "bottom"
  if (side === "bottom" && !fitsBelow && fitsAbove) side = "top"
  else if (side === "top" && !fitsAbove) side = "bottom"

  let left = trigger.left
  if (left + width > viewportWidth - margin) left = trigger.right - width
  return { left: clamp(left, width, viewportWidth), top: side === "bottom" ? below : above, side }
}

// Tooltip and popover panels are placed by CSS beside their trigger. When the
// open panel crosses the viewport, return the shift that fixes it: first the
// mirror side of the trigger (same gap) if that side fits; a side panel that
// fits on neither side goes below the trigger (else above) instead of covering
// it; then a clamp inside the margin. The side is read from the boxes, so no
// placement name is needed.
export function fitPopup(panel, trigger, { viewportWidth, viewportHeight }, { margin = 8 } = {}) {
  const fitsX = (l) => l >= margin && l + panel.width <= viewportWidth - margin
  const fitsY = (t) => t >= margin && t + panel.height <= viewportHeight - margin
  const clamp = (v, size, limit) => Math.min(Math.max(v, margin), Math.max(limit - size - margin, margin))
  let left = panel.left
  let top = panel.top
  // A side panel with no room on either side: under the trigger (else over
  // it), so it never covers the trigger that closes it.
  const stack = (gap) => {
    const below = trigger.bottom + gap
    top = fitsY(below) ? below : clamp(trigger.top - gap - panel.height, panel.height, viewportHeight)
    return clamp(left, panel.width, viewportWidth)
  }

  if (!fitsX(left)) {
    if (panel.left >= trigger.right - 1) {
      const gap = panel.left - trigger.right
      const mirror = trigger.left - gap - panel.width
      left = fitsX(mirror) ? mirror : stack(gap)
    } else if (panel.right <= trigger.left + 1) {
      const gap = trigger.left - panel.right
      const mirror = trigger.right + gap
      left = fitsX(mirror) ? mirror : stack(gap)
    } else {
      left = clamp(left, panel.width, viewportWidth)
    }
  }
  if (!fitsY(top)) {
    if (panel.top >= trigger.bottom - 1) {
      const mirror = trigger.top - (panel.top - trigger.bottom) - panel.height
      top = fitsY(mirror) ? mirror : clamp(top, panel.height, viewportHeight)
    } else if (panel.bottom <= trigger.top + 1) {
      const mirror = trigger.bottom + (trigger.top - panel.bottom)
      top = fitsY(mirror) ? mirror : clamp(top, panel.height, viewportHeight)
    } else {
      top = clamp(top, panel.height, viewportHeight)
    }
  }
  const dx = Math.round(left - panel.left)
  const dy = Math.round(top - panel.top)
  return { dx, dy, moved: dx !== 0 || dy !== 0 }
}

// Classes a reset restores for the configured placement (0.94.4). When the
// server markup already sets its own margin on that side (a combobox renders
// mt-1), keep it instead of adding the default mt-2 next to it.
const SIDE_MARGIN = { top: /^mb-/, right: /^ml-/, bottom: /^mt-/, left: /^mr-/ }

export function resetPlacementClasses(placement, configured, serverClasses = []) {
  const side = SIDE_MARGIN[placement] ? placement : "bottom"
  const own = serverClasses.find((c) => SIDE_MARGIN[side].test(c))
  if (!own) return configured
  return [...configured.filter((c) => !SIDE_MARGIN[side].test(c)), own]
}
